import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message, Button, Row, Col } from "antd";
import { getInvoiceDetails } from "../../../services/AdminPortalAPIs";
import Invoice from "./Invoice";
import LoadingSpinner from "../../../components/reusable/LoadingSpinner";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {InvoiceData} from "src/layouts/AdminPortal/Subscriptions/ViewInvoiceInterface";


const ViewInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [exporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await getInvoiceDetails(invoiceId || "");
        if (res.data.status) {
          setInvoice(res.data.data);
        } else {
          message.error(res.data.message || "Failed to load invoice details");
        }
      } catch (err) {
        // eslint-disable-next-line no-console -- intentional error logging
        console.error("Error fetching invoice:", err);
        message.error("Failed to fetch invoice details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [invoiceId]);

  const handleExportPDF = async () => {
    if (!invoiceRef.current) {return;}

    const pdf = new jsPDF("p", "mm", "a4");

    const canvas = await html2canvas(invoiceRef.current, {
      scale: 2, // high resolution
      useCORS: true,
      scrollY: -window.scrollY, // capture what's in viewport too
    } as any);

    const imgData = canvas.toDataURL("image/png");

    // A4 page dimensions in mm
    const a4Width = pdf.internal.pageSize.getWidth();
    const a4Height = pdf.internal.pageSize.getHeight();

    // Convert pixels to mm at 96 DPI (standard screen)
    const pxToMm = (px: number) => (px * 25.4) / 96;
    const imgWidthMm = pxToMm(canvas.width);
    const imgHeightMm = pxToMm(canvas.height);

    // Scale image to cover full page while maintaining aspect ratio
    const scale = Math.min(a4Width / imgWidthMm, a4Height / imgHeightMm);
    const width = imgWidthMm * scale;
    const height = imgHeightMm * scale;

    const xOffset = (a4Width - width) / 2;
    const yOffset = (a4Height - height) / 2;

    pdf.addImage(imgData, "PNG", xOffset, yOffset, width, height);
    pdf.save(`Invoice-${invoice?.invoice_number || "Export"}.pdf`);
  };

  if (loading) {return <LoadingSpinner />;}
  if (!invoice)
    {return <p style={{ padding: 16 }}>Invoice not found or failed to load.</p>;}

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        {/* Left-aligned Go Back button */}
        <Col>
          <Button
            className="button button--primary"
            type="primary"
            aria-label="Go Back"
            onClick={() => navigate("/admin-portal/subscriptions")}
          >
            <span>&lt;&nbsp;</span> Go Back
          </Button>
        </Col>

        {/* Right-aligned Edit + Export buttons */}
        <Col>
          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              icon={<EditOutlined />}
              className="button button--primary"
              type="primary"
              aria-label="Edit Invoice"
              onClick={() =>
                navigate(`/admin-portal/invoice/edit/${invoiceId}`)
              }
            >
              Edit Invoice
            </Button>
            <Button
              icon={<DownloadOutlined />}
              className="button button--primary"
              type="primary"
              aria-label="Export Invoice as PDF"
              onClick={handleExportPDF}
              loading={exporting}
            >
              Export
            </Button>
          </div>
        </Col>
      </Row>

      <div ref={invoiceRef}>
        <Invoice data={invoice} />
      </div>
    </div>
  );
};

export default ViewInvoice;
